import React, { useContext, useEffect, useState } from "react";
import { Card, Button, ProgressBar, ButtonGroup } from "react-bootstrap";
import { Upload, Download, CheckCircle, BookOpen, Fingerprint } from "lucide-react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import GlobalContext from "../../context/GlobalContext";
import {
  importAttendance,
  SubmitImportAttendance,
  getFingerprintMachines,
  importFingerprintAttendance,
  submitFingerprintImport,
} from "../../api/AttendanceApi";
import swal from "../../services/swalConfig";

import { tr } from "../../i18n";
export default function ImportAttendance() {
  const { user, setLoading } = useContext(GlobalContext);
  const [importMode, setImportMode] = useState("standard"); // "standard" | "fingerprint"
  const [uploadedData, setUploadedData] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [importedFile, setImportedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // --- State khusus Fingerprint Import ---
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState("");
  const [fpSummary, setFpSummary] = useState(null);

  useEffect(() => {
    const loadMachines = async () => {
      const response = await getFingerprintMachines();
      if (response?.status === 200) {
        const list = response.data || [];
        setMachines(list);
        if (list.length > 0) {
          setSelectedMachine(list[0].key);
        }
      }
    };
    loadMachines();
  }, []);

  const resetUploadState = () => {
    setUploadedData([]);
    setImportedFile(null);
    setUploadProgress(0);
    setFpSummary(null);
    const fileInput = document.getElementById("importFile");
    if (fileInput) fileInput.value = "";
  };

  const handleModeChange = (mode) => {
    if (mode === importMode) return;
    setImportMode(mode);
    resetUploadState();
  };

  const handleDownloadTemplate = () => {
    const fileUrl = "/templates/attendance_template.xls";
    window.open(fileUrl, "_blank");
  };

  const handleFileUpload = async (file) => {
    const fileInput = document.getElementById("importFile");
    if (fileInput) fileInput.value = "";

    setUploadedData([]);
    setImportedFile(file);
    setUploadProgress(0);

    const companyId = user?.workspace?.company_id;
    if (!companyId) {
      alert(tr("attendance:error_company_id_not_found_in"));
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", companyId);

    try {
      setUploadProgress(1);
      const response = await importAttendance(formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (response.status === "success") {
        setUploadedData(response.validated_data || []);
        setUploadProgress(100);
      } else {
        const errorData = await response.json();
        alert(tr("attendance:validation_failed_value", { value: errorData.message || response.statusText }));
        setUploadedData([]);
        setUploadProgress(0);
      }
    } catch (error) {
      alert(tr("attendance:an_unexpected_error_occurred_during_file"));
      setUploadedData([]);
      setUploadProgress(0);
    }
  };

  const handleFingerprintFileUpload = async (file) => {
    if (!selectedMachine) {
      swal.fire({
        title: tr("attendance:select_a_fingerprint_machine"),
        text: tr("attendance:please_choose_which_fingerprint_machine_this"),
        icon: "warning",
      });
      return;
    }

    const fileInput = document.getElementById("importFile");
    if (fileInput) fileInput.value = "";

    setUploadedData([]);
    setFpSummary(null);
    setImportedFile(file);
    setUploadProgress(0);

    const companyId = user?.workspace?.company_id;
    if (!companyId) {
      alert(tr("attendance:error_company_id_not_found_in"));
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", companyId);
    formData.append("machine_type", selectedMachine);

    // uploadProgress hanya melacak durasi UPLOAD byte file (biasanya cepat).
    // Parsing + validasi ratusan/ribuan baris di server bisa makan beberapa
    // detik lagi SETELAH upload selesai — makanya perlu loading overlay
    // terpisah supaya user tidak mengira proses sudah selesai/gagal.
    setLoading(true);

    try {
      setUploadProgress(1);
      const response = await importFingerprintAttendance(formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (response?.status === "success") {
        setUploadedData(response.validated_data || []);
        setFpSummary(response.summary || null);
        setUploadProgress(100);
      } else {
        swal.fire({
          title: tr("attendance:validation_failed"),
          text: response?.message || tr("attendance:could_not_validate_the_uploaded_file"),
          icon: "error",
        });
        setUploadedData([]);
        setUploadProgress(0);
      }
    } catch (error) {
      alert(tr("attendance:an_unexpected_error_occurred_during_file"));
      setUploadedData([]);
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessValid = async () => {
    const validRows = uploadedData.filter((row) => !row.error);

    if (validRows.length === 0) {
      swal.fire({
        title: tr("shared:no_valid_data"),
        text: tr("shared:there_are_no_valid_rows_to"),
        icon: "warning",
      });
      return;
    }

    const companyId = user?.workspace?.company_id;
    if (!companyId) {
      swal.fire({
        title: tr("shared:company_not_found"),
        text: tr("attendance:company_id_is_missing_in_user"),
        icon: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        company_id: companyId,
        records: validRows,
      };

      const response = await SubmitImportAttendance(payload);
      const data = response;

      if (data.status != 200) {
        swal.fire({
          title: tr("shared:error"),
          text: response.message,
          icon: "error",
        });
        return;
      }

      if (data.status === 200) {
        swal.fire({
          title: tr("shared:success"),
          text: `${data.message} (${data.count} records)`,
          icon: "success",
        });

        resetUploadState();
        return;
      }

      swal.fire({
        title: tr("common:errors.oops"),
        text: data.message || tr("shared:something_went_wrong"),
        icon: "error",
      });
    } catch (err) {
      swal.fire({
        title: tr("shared:unexpected_error"),
        text: tr("attendance:an_unexpected_error_occurred_while_processing"),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProcessFingerprintValid = async () => {
    const validRows = uploadedData.filter(
      (row) =>
        !row.error &&
        !row.already_exists &&
        (!row.no_schedule || row.marked_overtime),
    );

    if (validRows.length === 0) {
      swal.fire({
        title: tr("shared:no_valid_data"),
        text: tr("shared:there_are_no_valid_rows_to"),
        icon: "warning",
      });
      return;
    }

    const companyId = user?.workspace?.company_id;
    if (!companyId) {
      swal.fire({
        title: tr("shared:company_not_found"),
        text: tr("attendance:company_id_is_missing_in_user"),
        icon: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        company_id: companyId,
        machine_type: selectedMachine,
        // Baris yang ditandai "Change as Overtime" ikut dikirim di request yang
        // SAMA, cuma dibedakan lewat import_as — satu kali proses submit untuk
        // semuanya, bukan dua kali klik/dua kali insert terpisah.
        records: validRows.map((row) =>
          row.marked_overtime ? { ...row, import_as: "overtime" } : row,
        ),
      };

      const response = await submitFingerprintImport(payload);

      if (response?.status !== 200) {
        swal.fire({
          title: tr("shared:error"),
          text: response?.message || tr("shared:something_went_wrong"),
          icon: "error",
        });
        return;
      }

      swal.fire({
        title: tr("shared:success"),
        text: tr("attendance:message_affected_employees_employees_affected", { message: response.message, employees_affected: response.employees_affected }),
        icon: "success",
      });

      resetUploadState();
    } catch (err) {
      swal.fire({
        title: tr("shared:unexpected_error"),
        text: tr("attendance:an_unexpected_error_occurred_while_processing"),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Baris yang scan-nya jatuh di hari tanpa jadwal kerja (mis. hari libur bagi
  // karyawan itu) TIDAK langsung di-submit di sini — cuma ditandai lokal
  // (marked_overtime) supaya ikut kehitung di "Process Valid" dan baru benar-benar
  // di-insert saat tombol itu diklik, dalam satu proses yang sama dengan baris
  // attendance biasa. Ini murni perubahan status di layar, belum ada apa pun yang
  // masuk ke database.
  const handleMarkAsOvertime = () => {
    const eligibleCount = uploadedData.filter(
      (row) => row.no_schedule && !row.error && !row.marked_overtime,
    ).length;

    if (eligibleCount === 0) {
      return;
    }

    setUploadedData((prev) =>
      prev.map((row) =>
        row.no_schedule && !row.error
          ? { ...row, marked_overtime: true }
          : row,
      ),
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (importMode === "fingerprint") {
        handleFingerprintFileUpload(file);
      } else {
        handleFileUpload(file);
      }
      e.dataTransfer.clearData();
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (importMode === "fingerprint") {
      handleFingerprintFileUpload(file);
    } else {
      handleFileUpload(file);
    }
  };

  const validCount = uploadedData.filter(
    (x) =>
      !x.error && !x.already_exists && (!x.no_schedule || x.marked_overtime),
  ).length;
  // Baris hari-libur yang BELUM ditandai overtime — ini yang jadi angka di
  // tombol "Change as Overtime". Begitu ditandai, ikut pindah ke validCount.
  const overtimeCandidateCount = uploadedData.filter(
    (x) => x.no_schedule && !x.error && !x.marked_overtime,
  ).length;
  const isFingerprintMode = importMode === "fingerprint";
  const selectedMachineInfo = machines.find((m) => m.key === selectedMachine);

  const handleShowTutorial = () => {
    const driverObj = driver({
      showProgress: true,
      steps: [
        {
          element: "#tutorial-download-template",
          popover: {
            title: tr("shared:step_1_download_template"),
            description:
              tr("attendance:start_by_downloading_the_excel_template"),
            side: "left",
            align: "start",
          },
        },
        {
          element: "#tutorial-upload-area",
          popover: {
            title: tr("attendance:step_2_upload_your_file"),
            description:
              tr("attendance:click_or_drag_your_filled_excel"),
          },
        },
        {
          element: "#tutorial-data-preview",
          popover: {
            title: tr("attendance:step_3_review_data_preview"),
            description:
              tr("attendance:after_upload_review_the_table_below"),
          },
        },
        {
          element: "#tutorial-process-button",
          popover: {
            title: tr("attendance:step_4_process_valid_data"),
            description:
              tr("attendance:once_you_ve_reviewed_or_fixed"),
            side: "left",
            align: "start",
          },
        },
      ],
    });

    driverObj.drive();
  };

  return (
    <div className="container py-4">
      {/* Mode Toggle */}
      <div className="mb-4">
        <ButtonGroup>
          <Button
            variant={
              importMode === "standard" ? "primary" : "outline-primary"
            }
            onClick={() => handleModeChange("standard")}
          >
            {tr("attendance:standard_template")}
          </Button>
          <Button
            variant={
              importMode === "fingerprint" ? "primary" : "outline-primary"
            }
            onClick={() => handleModeChange("fingerprint")}
          >
            <Fingerprint size={16} className="me-1" />
            {tr("attendance:fingerprint_import")}
          </Button>
        </ButtonGroup>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2"
          onClick={handleShowTutorial}
        >
          <BookOpen size={16} />
          {tr("shared:show_tutorial")}
        </Button>

        {!isFingerprintMode && (
          <Button
            variant="outline-primary"
            className="d-flex align-items-center gap-2"
            id="tutorial-download-template"
            onClick={handleDownloadTemplate}
          >
            <Download size={16} />
            {tr("shared:download_template")}
          </Button>
        )}

        {isFingerprintMode && selectedMachineInfo?.template_url && (
          <Button
            variant="outline-primary"
            className="d-flex align-items-center gap-2"
            id="tutorial-download-template"
            onClick={() =>
              window.open(selectedMachineInfo.template_url, "_blank")
            }
          >
            <Download size={16} />
            {tr("attendance:download_sample_format")}{selectedMachineInfo.label})
          </Button>
        )}
      </div>

      {isFingerprintMode && (
        <Card className="border-0 shadow-sm p-4 mb-4">
          <label className="form-label fw-semibold mb-2">
            {tr("attendance:fingerprint_machine")}
          </label>
          <select
            className="form-select"
            style={{ maxWidth: "400px" }}
            value={selectedMachine}
            onChange={(e) => setSelectedMachine(e.target.value)}
          >
            {machines.length === 0 && (
              <option value="">{tr("attendance:loading_machines")}</option>
            )}
            {machines.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
          <small className="text-muted mt-2 d-block">
            {tr("attendance:upload_the_raw_daily_attendance_export")}
          </small>
        </Card>
      )}

      {/* Dropzone */}
      <Card className="border-0 shadow-sm p-4 mb-4">
        <div
          id="tutorial-upload-area"
          className={`w-100 p-4 text-center border border-2 border-dashed rounded position-relative ${
            isDragging ? "border-primary bg-light" : ""
          }`}
          style={{ borderStyle: "dashed", cursor: "pointer" }}
          onClick={() => document.getElementById("importFile").click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Upload size={40} className="text-primary mb-2" />
          <p className="fw-semibold mb-0">
            {tr("shared:click_here_to_upload_your_excel")}
          </p>
          <small className="text-muted">{tr("shared:supported_format_xls_xlsx")}</small>
        </div>

        <input
          type="file"
          accept=".xls,.xlsx"
          id="importFile"
          hidden
          onChange={handleInputChange}
        />
      </Card>

      {isFingerprintMode && fpSummary && (
        <Card className="border-0 shadow-sm p-3 mb-4">
          <div className="d-flex gap-4 flex-wrap">
            <div>
              <small className="text-muted d-block">{tr("attendance:total_rows_in_file")}</small>
              <strong>{fpSummary.total_rows}</strong>
            </div>
            <div>
              <small className="text-muted d-block">
                {tr("attendance:skipped_by_machine_absent_excluded")}
              </small>
              <strong>{fpSummary.skipped_by_machine}</strong>
            </div>
            <div>
              <small className="text-muted d-block">{tr("attendance:ready_to_import")}</small>
              <strong className="text-success">{fpSummary.ready}</strong>
            </div>
            <div>
              <small className="text-muted d-block">{tr("attendance:invalid_see_below")}</small>
              <strong className="text-danger">{fpSummary.invalid}</strong>
            </div>
            <div>
              <small className="text-muted d-block">
                {tr("attendance:day_off_no_schedule")}
              </small>
              <strong className="text-warning">
                {fpSummary.no_schedule ?? 0}
              </strong>
            </div>
            <div>
              <small className="text-muted d-block">
                {tr("attendance:already_recorded_will_be_skipped")}
              </small>
              <strong className="text-secondary">
                {fpSummary.already_exists ?? 0}
              </strong>
            </div>
          </div>
        </Card>
      )}

      {/* Preview */}
      <Card className="border-0 shadow-sm p-3" id="tutorial-data-preview">
        <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
          <h6 className="fw-bold m-0">{tr("shared:uploaded_data_preview")}</h6>

          <div className="d-flex gap-2">
            {isFingerprintMode && overtimeCandidateCount > 0 && (
              <Button
                variant="outline-warning"
                className="px-3"
                onClick={handleMarkAsOvertime}
              >
                {tr("attendance:change_as_overtime")}{overtimeCandidateCount})
              </Button>
            )}

            <Button
              variant="primary"
              className="px-4"
              disabled={validCount === 0}
              id="tutorial-process-button"
              onClick={
                isFingerprintMode
                  ? handleProcessFingerprintValid
                  : handleProcessValid
              }
            >
              {tr("shared:process_valid")}{validCount})
            </Button>
          </div>
        </div>

        <div className="table-responsive">
          <table>
            <thead>
              {isFingerprintMode ? (
                <tr>
                  <th>#</th>
                  <th>{tr("shared:internal_id")}</th>
                  <th>{tr("shared:name")}</th>
                  <th>{tr("attendance:date")}</th>
                  <th>{tr("attendance:time_in")}</th>
                  <th>{tr("attendance:time_out")}</th>
                  <th>{tr("shared:message")}</th>
                </tr>
              ) : (
                <tr>
                  <th>#</th>
                  <th>{tr("attendance:employee_id_email")}</th>
                  <th>{tr("attendance:date")}</th>
                  <th>{tr("attendance:time_in")}</th>
                  <th>{tr("attendance:time_out")}</th>
                  <th>{tr("shared:message")}</th>
                </tr>
              )}
            </thead>

            <tbody>
              {uploadProgress > 0 && uploadProgress < 100 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <ProgressBar
                      now={100}
                      label={`Processing...`}
                      animated
                      striped
                      variant="primary"
                      style={{ height: "25px" }}
                    />
                  </td>
                </tr>
              ) : uploadedData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-5">
                    {tr("shared:no_data_imported_yet")}
                  </td>
                </tr>
              ) : (
                uploadedData.map((row, index) => (
                  <tr
                    key={index}
                    className={
                      row.error
                        ? "table-danger"
                        : row.no_schedule && !row.marked_overtime
                          ? "table-warning"
                          : row.already_exists
                            ? "table-secondary"
                            : ""
                    }
                  >
                    <td>{index + 1}</td>
                    {isFingerprintMode ? (
                      <>
                        <td>{row.internal_id}</td>
                        <td>{row.name}</td>
                      </>
                    ) : (
                      <td>{row.employee_id_or_email}</td>
                    )}
                    <td>{row.date}</td>
                    <td>{row.time_in ?? row.timeIn}</td>
                    <td>{row.time_out ?? row.timeOut}</td>
                    <td>
                      {row.error ? (
                        <span className="text-danger d-flex align-items-center gap-1">
                          <ul className="mb-0">
                            {row.error.map((errorMessage, idx) => (
                              <li key={idx}>{errorMessage}</li>
                            ))}
                          </ul>
                        </span>
                      ) : row.no_schedule && row.marked_overtime ? (
                        <div
                          className="d-flex align-items-center gap-1"
                          style={{ paddingLeft: "1rem" }}
                        >
                          <CheckCircle size={14} className="text-success" />
                          <span className="text-success text-sm">
                            {" "}
                            {tr("attendance:will_be_imported_as_holiday_overtime")}
                          </span>
                        </div>
                      ) : row.no_schedule ? (
                        <span className="text-warning">
                          {tr("attendance:no_schedule_for_this_day_day")}
                        </span>
                      ) : row.already_exists ? (
                        <span className="text-secondary">
                          {tr("attendance:attendance_already_recorded_for_this_date")}
                        </span>
                      ) : (
                        <div
                          className="d-flex align-items-center gap-1"
                          style={{ paddingLeft: "1rem" }}
                        >
                          <CheckCircle size={14} className="text-success" />
                          <span className="text-success text-sm"> {tr("shared:valid")}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
